import { Alert, AlertTitle, Box, Button, Stack, Typography } from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HistoryIcon from "@mui/icons-material/History";
import dayjs from "dayjs";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";

interface CoReviewBannerProps {
  comments: CoReviewComment[];
  returnedAt?: string | null;
  onViewHistory?: () => void;
}

export function CoReviewBanner({
  comments,
  returnedAt,
  onViewHistory,
}: CoReviewBannerProps) {
  // Replies aren't independent action items — only count top-level threads.
  const topLevel = comments.filter((c) => !c.parentCommentId);
  const unresolved = topLevel.filter((c) => !c.resolvedAt);
  const total = topLevel.length;
  const sectionsWithComments = new Set(topLevel.map((c) => c.sectionPath)).size;
  const co = comments[0]?.authorName ?? "Your CO";

  const actions = onViewHistory ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        color="warning"
        size="small"
        variant="outlined"
        startIcon={<HistoryIcon />}
        onClick={onViewHistory}
      >
        View history
      </Button>
    </Stack>
  ) : undefined;

  return (
    <Alert
      severity="warning"
      icon={<ReportProblemOutlinedIcon fontSize="inherit" />}
      sx={{
        mb: 2,
        borderRadius: 2,
        alignItems: "center",
      }}
      action={actions}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {co} rejected this report
      </AlertTitle>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <Typography variant="body2">
          {total} comment{total === 1 ? "" : "s"} across {sectionsWithComments} section
          {sectionsWithComments === 1 ? "" : "s"}
          {unresolved.length > 0 ? ` · ${unresolved.length} still open` : " · all resolved"}
        </Typography>
        {returnedAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Rejected {dayjs(returnedAt).format("MMM D, h:mm A")}
            </Typography>
          </Box>
        )}
      </Stack>
    </Alert>
  );
}
